import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { useAccent, ranuButtonSx, ranuTextButtonSx } from "./SectionCard";

// Search (and the "Add Devotee" button) only start after this many characters
const MIN_SEARCH_CHARS = 3;

const DevoteeSearch = ({
  devoteeOptions,
  isLoading,
  isNewDevotee,
  onSearch,
  onDevoteeSelect,
  onNewDevoteeToggle,
}) => {
  const { accentName } = useAccent();

  // Only used to decide when the "Add Devotee" button is shown
  const [inputValue, setInputValue] = useState("");
  const [hasSelection, setHasSelection] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // The Autocomplete clears its input as soon as it loses focus (e.g. when the Add Devotee
  // button is clicked), so the text the user really typed is kept here
  const typedTextRef = useRef("");

  const hasEnoughChars = inputValue.trim().length >= MIN_SEARCH_CHARS;

  const noResults = !isLoading && devoteeOptions.length === 0;

  // The button below the box appears as soon as 3+ characters are typed (and no devotee is
  // selected). While the dropdown shows "No devotees found" it has its own Add Devotee button,
  // so the one below is hidden then to avoid two buttons.
  const showAddButton = !isNewDevotee && !hasSelection && hasEnoughChars && !(isOpen && noResults);

  // The parent handler still reads event.target.checked, so the same shape is passed
  // The typed search text is passed along so the new devotee form can be prefilled with it
  const startNewDevotee = () => {
    const searchText = typedTextRef.current || inputValue;
    typedTextRef.current = "";
    setInputValue("");
    setHasSelection(false);
    onNewDevoteeToggle({ target: { checked: true } }, searchText);
  };

  const backToSearch = () => {
    setInputValue("");
    setHasSelection(false);
    onNewDevoteeToggle({ target: { checked: false } });
  };

  if (isNewDevotee) {
    return (
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <MDTypography variant="button" fontWeight="medium" color={accentName}>
          New devotee
        </MDTypography>
        <MDButton
          variant="text"
          color={accentName}
          size="small"
          onClick={backToSearch}
          sx={ranuTextButtonSx}
        >
          Back to search
        </MDButton>
      </MDBox>
    );
  }

  return (
    <MDBox mb={2}>
      <MDBox mb={0.75}>
        <MDTypography variant="button" fontWeight="medium" color={accentName}>
          Search devotee
        </MDTypography>
      </MDBox>
      <Autocomplete
        options={devoteeOptions}
        getOptionLabel={(option) => `${option.first_name} ${option.last_name} - ${option.phone}`}
        onChange={(event, newValue) => {
          setHasSelection(!!newValue);
          onDevoteeSelect(newValue);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") typedTextRef.current = newInputValue;
          setInputValue(newInputValue);
          // Below the minimum, search with an empty term: the parent clears the old results
          onSearch(newInputValue.trim().length >= MIN_SEARCH_CHARS ? newInputValue : "");
        }}
        loading={isLoading}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        renderInput={(params) => (
          <MDInput {...params} placeholder="Enter phone number or name" fullWidth />
        )}
        noOptionsText={
          // Shown inside the dropdown, so the button is never hidden behind the list
          hasEnoughChars ? (
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <MDTypography variant="button" color="text">
                No devotees found
              </MDTypography>
              <MDButton color="primary" size="small" onClick={startNewDevotee} sx={ranuButtonSx}>
                Add Devotee
              </MDButton>
            </MDBox>
          ) : (
            <MDTypography variant="button" color="text">
              Type at least {MIN_SEARCH_CHARS} characters to search
            </MDTypography>
          )
        }
        loadingText="Searching..."
      />

      {showAddButton && (
        <MDBox mt={2} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
          <MDTypography variant="button" color="text">
            {noResults
              ? "No devotee found for this search."
              : "Can't find the devotee in the list?"}
          </MDTypography>
          <MDButton color="primary" size="small" onClick={startNewDevotee} sx={ranuButtonSx}>
            Add Devotee
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  );
};

DevoteeSearch.propTypes = {
  devoteeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      devotee_id: PropTypes.number.isRequired,
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isNewDevotee: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
  onDevoteeSelect: PropTypes.func.isRequired,
  onNewDevoteeToggle: PropTypes.func.isRequired,
};

export default DevoteeSearch;
